/**
 * Seasonal Pricing Dialog Component
 * Handles multiple date range entries for seasonal pricing
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarDays, Plus, X } from 'lucide-react';

export default function SeasonalPricingDialog({
  open,
  onOpenChange,
  selectedPricingRule,
  formData,
  setFormData,
  onSubmit
}) {
  const addEntry = () => {
    setFormData(prev => ({
      ...prev,
      seasonal_prices: [...prev.seasonal_prices, { 
        id: `new-${Date.now()}`, 
        start_date: '', 
        end_date: '', 
        price: '' 
      }]
    }));
  };

  const removeEntry = (id) => {
    setFormData(prev => ({
      ...prev,
      seasonal_prices: prev.seasonal_prices.filter(p => p.id !== id)
    }));
  };

  const updateEntry = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      seasonal_prices: prev.seasonal_prices.map(p => 
        p.id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-blue-500" />
            Seasonal Pricing
          </DialogTitle>
          <DialogDescription>
            Set multiple date ranges with different prices for "{selectedPricingRule?.name}"
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-3">
            {formData.seasonal_prices.map((entry, index) => (
              <div key={entry.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Period {index + 1}</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 text-red-500"
                    onClick={() => removeEntry(entry.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">From Date</Label>
                    <Input 
                      type="date" 
                      value={entry.start_date || ''} 
                      onChange={(e) => updateEntry(entry.id, 'start_date', e.target.value)} 
                      className="border-orange-200 text-sm" 
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">To Date</Label>
                    <Input 
                      type="date" 
                      value={entry.end_date || ''} 
                      onChange={(e) => updateEntry(entry.id, 'end_date', e.target.value)} 
                      min={entry.start_date || undefined}
                      className="border-orange-200 text-sm" 
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Price (Rs)</Label>
                    <Input 
                      type="number" 
                      value={entry.price || ''} 
                      onChange={(e) => updateEntry(entry.id, 'price', e.target.value)} 
                      className="border-orange-200 text-sm" 
                      placeholder="0"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}
            
            {formData.seasonal_prices.length === 0 && (
              <div className="text-center py-4 text-gray-400 text-sm">
                No seasonal pricing entries. Click "Add Period" to create one.
              </div>
            )}
            
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={addEntry}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Period
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Each period applies a specific price during those dates. Prices override the base price.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-gradient-orange text-white">Save Seasonal Pricing</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
